import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';

import { useStripe, useElements, PaymentElement, ExpressCheckoutElement } from '@stripe/react-stripe-js';

// Helper function to add payment failure URL parameter
const addPaymentFailureParameter = () => {
  const url = new URL(window.location);
  if (!url.searchParams.has('payment-failure')) {
    url.searchParams.set('payment-failure', '');
    window.history.replaceState({}, '', url);
  }
};

// Helper function to remove payment failure URL parameter
const removePaymentFailureParameter = () => {
  const url = new URL(window.location);
  if (url.searchParams.has('payment-failure')) {
    url.searchParams.delete('payment-failure');
    window.history.replaceState({}, '', url);
  }
};

export default function CheckoutForm({ uid, customerEmail, stripeCustomerId, setParentSubmitting, isParentSubmitting, onStatusChange, selectedPlan, isAuthorizationFlow, authPaymentIntentId }) {
  const stripe = useStripe();
  const elements = useElements();

  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState(null);
  const [status, setStatus] = useState(null); // 'success', 'error', or null
  const [showAuthInfo, setShowAuthInfo] = useState(false);
  const [customerName, setCustomerName] = useState('');
  const [expressReady, setExpressReady] = useState(false);

  // Monitor status changes and notify parent component
  useEffect(() => {
    if (onStatusChange && status) {
      onStatusChange(status);
    }
  }, [status, onStatusChange]);

  // Monitor error state to update parent component
  useEffect(() => {
    if (status === 'error' && setParentSubmitting) {
      setParentSubmitting(false);
    }
  }, [status, setParentSubmitting]);

  // Clean up payment failure parameter when component unmounts
  useEffect(() => {
    return () => {
      removePaymentFailureParameter();
    };
  }, []);

  // Shared post-confirmation logic for both card form and express checkout
  const handlePostConfirmation = async () => {
    // For authorization flow: card is now authorized, create the trial subscription
    if (isAuthorizationFlow && authPaymentIntentId) {
      try {
        const trialResponse = await fetch('/api/create-authorization-hold', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            uid,
            customerId: stripeCustomerId,
            paymentIntentId: authPaymentIntentId
          })
        });

        if (!trialResponse.ok) {
          const trialError = await trialResponse.json();
          console.error('Trial subscription creation failed:', trialError);
          setMessage(`Authorization succeeded but trial setup failed: ${trialError.message || trialError.error}`);
          setStatus('error');
          addPaymentFailureParameter();
          return false;
        }

        const trialData = await trialResponse.json();
        console.log('✅ Trial subscription created:', trialData.subscriptionId);
      } catch (trialErr) {
        console.error('Error creating trial subscription:', trialErr);
        setMessage(`Authorization succeeded but trial setup failed: ${trialErr.message}`);
        setStatus('error');
        addPaymentFailureParameter();
        return false;
      }
    }

    setMessage(selectedPlan?.hasTrial || isAuthorizationFlow ? "Starting your free trial..." : "Subscription created successfully!");
    setStatus('success');
    removePaymentFailureParameter();

    try {
      const response = await fetch('/api/setIsPremiumDirect', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          uid,
          source: 'direct_checkout_confirmation',
          subscriptionStatus: selectedPlan?.hasTrial ? 'trialing' : 'active'
        }),
      });
      if (!response.ok) {
        console.error('❌ Failed to set isPremium directly');
      }
    } catch (directSetError) {
      console.error('❌ Error setting isPremium directly:', directSetError);
    }

    const cacheKey = `premium_status_${uid}`;
    sessionStorage.setItem(cacheKey, JSON.stringify({
      eligible: true,
      timestamp: Date.now(),
      source: 'payment_confirmation',
      subscriptionStatus: selectedPlan?.hasTrial ? 'trialing' : 'active'
    }));

    try {
      const { syncStripeSuccessWithRevenueCat } = await import('../utils/revenueCatSync');
      const revenueCatSync = await syncStripeSuccessWithRevenueCat({
        firebaseUid: uid,
        userEmail: customerEmail,
        priceId: selectedPlan?.id
      });
      if (!revenueCatSync.success) {
        console.warn(`⚠️ RevenueCat checkout sync failed for ${uid}:`, revenueCatSync.error);
      }
    } catch (syncError) {
      console.error(`❌ RevenueCat checkout sync error for ${uid}:`, syncError);
    }

    setTimeout(() => {
      window.location.href = '/overview?redirect-purchase';
    }, 1500);

    return true;
  };

  // Express Checkout confirm handler (Apple Pay / Google Pay / Link)
  const handleExpressCheckoutConfirm = async () => {
    if (!stripe || !elements) return;

    setIsLoading(true);
    setMessage(null);
    setStatus(null);
    if (setParentSubmitting) setParentSubmitting(true);

    try {
      const { error: submitError } = await elements.submit();
      if (submitError) {
        setMessage(submitError.message);
        setStatus('error');
        addPaymentFailureParameter();
        return;
      }

      const { error: confirmError } = await stripe.confirmPayment({
        elements,
        confirmParams: {
          return_url: `${window.location.origin}/overview?redirect-purchase`,
        },
        redirect: 'if_required'
      });

      if (confirmError) {
        console.error('Express checkout confirmation error:', confirmError);
        setMessage(confirmError.message);
        setStatus('error');
        addPaymentFailureParameter();
        return;
      }

      await handlePostConfirmation();
    } catch (err) {
      console.error('Error during express checkout:', err);
      setMessage(`Payment failed: ${err.message}`);
      setStatus('error');
      addPaymentFailureParameter();
    } finally {
      setIsLoading(false);
      if (setParentSubmitting) setParentSubmitting(false);
    }
  };

  // Handle form submission (card payment)
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!stripe || !elements) {
      console.error('Stripe has not loaded yet');
      return;
    }

    setIsLoading(true);
    setMessage(null);
    setStatus(null);
    if (setParentSubmitting) setParentSubmitting(true);

    try {
      const { error: confirmError } = await stripe.confirmPayment({
        elements,
        confirmParams: {
          return_url: `${window.location.origin}/overview?redirect-purchase`,
        },
        redirect: 'if_required'
      });

      if (confirmError) {
        console.error('Payment confirmation error:', confirmError);
        setMessage(confirmError.message);
        setStatus('error');
        addPaymentFailureParameter();
        return;
      }

      await handlePostConfirmation();
    } catch (err) {
      console.error('Error during payment process:', err);
      setMessage(`Payment failed: ${err.message}`);
      setStatus('error');
      addPaymentFailureParameter();
    } finally {
      setIsLoading(false);
      if (setParentSubmitting) setParentSubmitting(false);
    }
  };

  // Create subscription for paid plans
  const createSubscriptionForPaidPlan = async (paymentMethodId) => {
    
    if (!paymentMethodId) {
      throw new Error('Payment method ID is required for paid plans');
    }
    
    const response = await fetch('/api/create-subscription', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        paymentMethodId: paymentMethodId,
        customerId: stripeCustomerId,
        priceId: selectedPlan.id,
        uid,
        email: customerEmail
      })
    });

    if (!response.ok) {
      const errorData = await response.json();
      console.error('Subscription creation failed:', errorData);
      throw new Error(errorData.error || 'Failed to create subscription');
    }

    const result = await response.json();
    
    return result;
  };

  // Create trial subscription with authorized payment method
  const createTrialSubscription = async (paymentMethodId) => {
    
    if (!paymentMethodId) {
      throw new Error('Payment method ID is required for trial subscription');
    }
    
    const response = await fetch('/api/create-subscription', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        paymentMethodId: paymentMethodId, // Make sure this is properly passed
        customerId: stripeCustomerId,
        priceId: selectedPlan.id,
        uid,
        email: customerEmail
      })
    });

    if (!response.ok) {
      const errorData = await response.json();
      console.error('Trial subscription creation failed:', errorData);
      throw new Error(errorData.error || 'Failed to create trial subscription');
    }

    const result = await response.json();
    return result;
  };

  // Close modal handler
  const closeAuthInfoModal = () => {
    setShowAuthInfo(false);
  };

  // Show authorization info modal
  const showAuthInfoModal = () => {
    setShowAuthInfo(true);
  };

  return (
    <form id="payment-form" onSubmit={handleSubmit}>
      {/* Express Checkout: Apple Pay, Google Pay, Link — renders only when available */}
      <div 
        style={{ 
          minHeight: expressReady ? 'auto' : '0px',
          overflow: 'hidden',
          transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
          opacity: expressReady ? 1 : 0,
          transform: expressReady ? 'translateY(0)' : 'translateY(-8px)',
        }}
      >
        <ExpressCheckoutElement
          onConfirm={handleExpressCheckoutConfirm}
          onReady={({ availablePaymentMethods }) => {
            if (availablePaymentMethods) setExpressReady(true);
          }}
          options={{
            buttonHeight: 48,
            buttonTheme: {
              applePay: 'white-outline',
              googlePay: 'white',
            },
            buttonType: {
              applePay: 'subscribe',
              googlePay: 'subscribe',
            },
            layout: {
              maxColumns: 2,
              maxRows: 1,
            },
            paymentMethods: {
              applePay: 'always',
              googlePay: 'always',
            },
          }}
        />
      </div>

      {/* Divider between express and card */}
      {expressReady && (
        <div className="flex items-center gap-4 my-5">
          <div className="flex-1 h-px bg-gradient-to-r from-transparent via-white/20 to-transparent"></div>
          <span className="text-white/40 text-xs font-medium tracking-wider uppercase">or pay with card</span>
          <div className="flex-1 h-px bg-gradient-to-r from-transparent via-white/20 to-transparent"></div>
        </div>
      )}

      <PaymentElement 
        id="payment-element"
        options={{
          layout: "accordion",
          defaultCollapsed: expressReady,
          radios: false,
          defaultValues: {
            billingDetails: {
              name: customerName,
            },
          },
        }}
      />
        
      {message && (
        <div className={`mt-4 p-3 rounded-lg text-sm ${
          status === 'error' 
            ? 'bg-red-500/20 text-red-300 border border-red-400/30' 
            : 'bg-green-500/20 text-green-300 border border-green-400/30'
        }`}>
          {message}
        </div>
      )}

                <button
        id="submit" 
        disabled={isLoading || isParentSubmitting || !stripe || !elements}
        className={`w-full py-4 px-6 rounded-xl font-medium transition-all duration-300 transform hover:scale-105 disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:scale-100 mt-6 ${
          selectedPlan?.period === 'Monthly'
            ? 'bg-gradient-to-r from-green-500/30 to-emerald-500/30 hover:from-green-500/40 hover:to-emerald-500/40 text-green-300 border border-green-400/30 hover:border-green-400/50' 
            : 'bg-gradient-to-r from-purple-500/30 to-blue-500/30 hover:from-purple-500/40 hover:to-blue-500/40 text-white border border-purple-400/30 hover:border-purple-400/50'
        }`}
        style={{ 
          boxShadow: `inset 0 1px 0 rgba(255,255,255,0.1), 0 8px 32px rgba(${selectedPlan?.period === 'Monthly' ? '34, 197, 94' : '147, 51, 234'}, 0.3)` 
        }}
        type="submit"
      >
        <span id="button-text">
          {(isLoading || isParentSubmitting) ? (
            <div className="flex items-center justify-center">
              <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 004 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
              Processing...
            </div>
          ) : (
            selectedPlan?.hasTrial ? 'Start Free Trial' : 'Complete Purchase'
          )}
        </span>
                </button>
                
      {/* Authorization Notice for Free Trial - Moved below button */}
      {selectedPlan?.hasTrial && (
        <div className="mt-4 p-4 bg-blue-500/10 border border-blue-400/30 rounded-lg">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <div className="flex items-center mb-2">
                <svg className="w-5 h-5 text-blue-400 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                </svg>
                <h4 className="text-sm font-medium text-blue-300">Card Authorization Required</h4>
              </div>
              <p className="text-sm text-blue-200 mb-2">
                Your card will be authorized for the subscription amount to validate it&apos;s ready for billing when your trial ends.
              </p>
              <p className="text-xs text-blue-300">
                ✅ You won&apos;t be charged during your <strong>{selectedPlan?.trialDays || 30}-day free trial</strong>
              </p>
            </div>
            <button
              type="button"
              onClick={showAuthInfoModal}
              className="ml-3 text-blue-400 hover:text-blue-300 transition-colors"
              aria-label="Learn more about card authorization"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
              </svg>
            </button>
          </div>
        </div>
      )}
                
      {/* Auth Info Modal */}
                {showAuthInfo && createPortal(
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50" style={{ zIndex: 9999 }}>
                    <div 
                      className="bg-gray-800 border border-gray-600 rounded-lg shadow-lg p-6 max-w-md mx-4 relative" 
            role="dialog"
            aria-modal="true"
            aria-labelledby="auth-info-title"
                      style={{ zIndex: 10000 }}
                    >
                      <div className="text-sm text-gray-200">
              <p id="auth-info-title" className="font-medium text-white mb-3">🛡️ Card Authorization & Fraud Protection</p>
              
              <div className="mb-4">
                <h4 className="font-medium text-blue-300 mb-2">Why we authorize your card:</h4>
                <ul className="list-disc list-inside space-y-1 text-gray-300 text-sm">
                  <li>Verify your card is valid and active</li>
                  <li>Prevent fraudulent sign-ups and protect our community</li>
                          <li>Ensure smooth billing when your trial ends</li>
                  <li>Reduce chargebacks and disputes</li>
                        </ul>
              </div>

              <div className="mb-4 p-3 bg-green-500/10 border border-green-400/30 rounded">
                <p className="text-green-400 text-sm font-medium mb-1">
                  ✅ You won&apos;t be charged during your {selectedPlan?.trialDays || 30}-day free trial
                </p>
                <p className="text-green-300 text-xs">
                  The authorization is automatically released if you cancel before your trial ends.
                </p>
              </div>

              <div className="mb-4 p-3 bg-yellow-500/10 border border-yellow-400/30 rounded">
                <h4 className="font-medium text-yellow-300 mb-2">⚠️ Avoid These Card Types:</h4>
                <ul className="list-disc list-inside space-y-1 text-yellow-200 text-xs">
                  <li><strong>Cash App cards</strong> - Often decline authorizations</li>
                  <li><strong>Privacy.com virtual cards</strong> - May block recurring charges</li>
                  <li><strong>Prepaid/gift cards</strong> - Usually insufficient for subscriptions</li>
                  <li><strong>Business cards with restrictions</strong> - May decline online subscriptions</li>
                </ul>
                <p className="text-yellow-300 text-xs mt-2">
                  Use a traditional debit or credit card from major banks for best results.
                          </p>
                        </div>

              <p className="text-gray-400 text-xs">
                This authorization helps us maintain a trustworthy platform and protect against fraudsters who might otherwise abuse free trials.
              </p>
                      </div>
                      <button
              onClick={closeAuthInfoModal}
              className="absolute top-2 right-2 text-gray-400 hover:text-white transition-colors"
              aria-label="Close dialog"
                        type="button"
                      >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
                        </svg>
                      </button>
                    </div>
                  </div>,
                  document.body
                )}
    </form>
  );
}

// PropTypes validation
CheckoutForm.propTypes = {
  uid: PropTypes.string,
  customerEmail: PropTypes.string,
  stripeCustomerId: PropTypes.string,
  setParentSubmitting: PropTypes.func,
  isParentSubmitting: PropTypes.bool,
  onStatusChange: PropTypes.func,
  selectedPlan: PropTypes.shape({
    id: PropTypes.string,
    hasTrial: PropTypes.bool,
    name: PropTypes.string,
    price: PropTypes.string,
    lines: PropTypes.number,
    period: PropTypes.string,
    isMessaging: PropTypes.bool
  }),
  isAuthorizationFlow: PropTypes.bool,
  authPaymentIntentId: PropTypes.string
};